<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDebounceFn } from '@vueuse/core';
import { useAlert } from 'dashboard/composables';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import BusinessAPI from 'dashboard/api/businessConnectors';
import ContactAPI from 'dashboard/api/contacts';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import FieldEditor from './FieldEditor.vue';
import { mergeResponseFields } from './fieldMapping';

const { t } = useI18n();
const records = ref([]);
const loading = ref(true);
const saving = ref(false);
const failed = ref(false);
const editingId = ref(null);
const deleteId = ref(null);
const testCustomerId = ref('');
const testContactId = ref('');
const testContacts = ref([]);
const contactSearchError = ref(false);
const { run: runContactSearch, isPending: contactsLoading } =
  useAbortableRequest();
const testing = ref(false);
const testResult = ref(null);
const testError = ref('');
const testDiagnostics = ref(null);
const paths = ref([]);
let testSequence = 0;
const DIAGNOSTICS_CLASS =
  'overflow-auto rounded-lg bg-n-alpha-2 p-3 text-xs text-n-slate-12 whitespace-pre-wrap break-all';
const field = (key, label, path, group = '') => ({
  key,
  label,
  path,
  group,
  type: 'text',
  visible: true,
  currency: 'CNY',
  status_map_text: '{}',
});
const blank = () => ({
  name: '',
  enabled: false,
  base_url: '',
  customer_path: '/customers/{customer_id}',
  auth_type: 'none',
  auth_header: 'X-API-Key',
  secret: '',
  username: '',
  clear_credentials: false,
  response_root: '',
  customer_id_path: 'id',
  customer_id_source: 'email',
  identifier_prefix: '',
  fields: [],
});
const form = ref(blank());
const customerIdSourceType = computed({
  get: () =>
    form.value.customer_id_source.startsWith('custom_attributes.')
      ? 'custom'
      : form.value.customer_id_source,
  set: value => {
    form.value.customer_id_source =
      value === 'custom' ? 'custom_attributes.' : value;
  },
});
const customCustomerIdField = computed({
  get: () => form.value.customer_id_source.slice('custom_attributes.'.length),
  set: value => {
    form.value.customer_id_source = `custom_attributes.${value}`;
  },
});
const testContactOptions = computed(() =>
  testContacts.value.map(contact => ({
    value: contact.id,
    label: contact.email ? `${contact.name} (${contact.email})` : contact.name,
  }))
);
const canTest = computed(() =>
  form.value.customer_id_source
    ? Boolean(testContactId.value)
    : Boolean(testCustomerId.value.trim())
);
const fetchTestContacts = async (query = '') => {
  contactSearchError.value = false;
  try {
    const response = await runContactSearch(signal =>
      query.trim()
        ? ContactAPI.search(query.trim(), 1, '-last_activity_at', '', {
            signal,
          })
        : ContactAPI.get(1, '-last_activity_at', '', { signal })
    );
    if (response) {
      const selectedContact = testContacts.value.find(
        contact => contact.id === testContactId.value
      );
      const contacts = response.data.payload;
      testContacts.value =
        selectedContact &&
        !contacts.some(contact => contact.id === selectedContact.id)
          ? [selectedContact, ...contacts]
          : contacts;
    }
  } catch {
    contactSearchError.value = true;
    testContacts.value = [];
  }
};
const searchTestContacts = useDebounceFn(fetchTestContacts, 300);
const currentTestConfiguration = () => ({
  base_url: form.value.base_url,
  customer_path: form.value.customer_path,
  auth_type: form.value.auth_type,
  auth_header: form.value.auth_header,
  response_root: form.value.response_root,
  customer_id_source: form.value.customer_id_source,
  secret: form.value.secret,
  username: form.value.username,
  clear_credentials: form.value.clear_credentials,
});
const errorText = error => {
  const key = `BUSINESS.ERRORS.${error.response?.data?.code || 'invalid_configuration'}`;
  return t(key);
};
const formatDiagnostics = details => JSON.stringify(details, null, 2);
watch(
  currentTestConfiguration,
  () => {
    testSequence += 1;
    testResult.value = null;
    testError.value = '';
    testDiagnostics.value = null;
    paths.value = [];
    testing.value = false;
  },
  { flush: 'sync' }
);
watch([testCustomerId, testContactId], () => {
  testSequence += 1;
  testResult.value = null;
  testError.value = '';
  testDiagnostics.value = null;
  testing.value = false;
});
const edit = record => {
  paths.value = [];
  editingId.value = record.id;
  form.value = {
    ...JSON.parse(JSON.stringify(record)),
    secret: '',
    username: '',
    clear_credentials: false,
    fields: record.fields.map(item => ({
      ...item,
      status_map_text: JSON.stringify(item.status_map || {}),
    })),
  };
};
const reset = () => {
  paths.value = [];
  editingId.value = null;
  form.value = blank();
};
const load = async () => {
  loading.value = true;
  failed.value = false;
  try {
    records.value = (await BusinessAPI.get()).data;
  } catch {
    failed.value = true;
  } finally {
    loading.value = false;
  }
};
const loadExample = () => {
  reset();
  form.value = {
    ...blank(),
    name: t('BUSINESS.DEMO_NAME'),
    base_url: 'http://business-demo:8090',
    response_root: 'data',
    customer_id_path: 'customer.id',
    customer_id_source: '',
    identifier_prefix: 'demo:user:',
    fields: [
      field(
        'name',
        t('BUSINESS.CUSTOMER_NAME'),
        'customer.nickname',
        t('BUSINESS.BASIC_GROUP')
      ),
      field(
        'email',
        t('BUSINESS.EMAIL'),
        'customer.email',
        t('BUSINESS.BASIC_GROUP')
      ),
      field(
        'plan',
        t('BUSINESS.PLAN'),
        'subscription.plan',
        t('BUSINESS.PLAN_GROUP')
      ),
      {
        ...field(
          'expires_at',
          t('BUSINESS.EXPIRY'),
          'subscription.expires_at',
          t('BUSINESS.PLAN_GROUP')
        ),
        type: 'datetime',
      },
      {
        ...field(
          'remaining_gb',
          t('BUSINESS.REMAINING_GB'),
          'subscription.remaining_gb',
          t('BUSINESS.PLAN_GROUP')
        ),
        type: 'number',
      },
    ],
  };
  testCustomerId.value = '10086';
};
const save = async () => {
  if (saving.value) return;
  saving.value = true;
  try {
    const body = { ...form.value };
    delete body.id;
    delete body.credential_configured;
    body.fields = body.fields.map(item => {
      const { status_map_text: statusText, ...values } = item;
      const statuses = JSON.parse(statusText || '{}');
      if (!statuses || Array.isArray(statuses) || typeof statuses !== 'object')
        throw new Error('Invalid status mapping');
      return { ...values, status_map: statuses };
    });
    const response = editingId.value
      ? await BusinessAPI.update(editingId.value, { connector: body })
      : await BusinessAPI.create({ connector: body });
    edit(response.data);
    records.value = (await BusinessAPI.get()).data;
    useAlert(t('BUSINESS.SAVED'));
  } catch (error) {
    useAlert(errorText(error));
  } finally {
    saving.value = false;
  }
};
const toggle = async record => {
  if (saving.value) return;
  saving.value = true;
  try {
    const { data } = await BusinessAPI.update(record.id, {
      connector: {
        enabled: !record.enabled,
        lock_version: record.lock_version,
      },
    });
    records.value = records.value.map(item =>
      item.id === data.id ? data : item
    );
    if (editingId.value === data.id) edit(data);
  } catch (error) {
    useAlert(errorText(error));
  } finally {
    saving.value = false;
  }
};
const remove = async record => {
  saving.value = true;
  try {
    await BusinessAPI.delete(record.id);
    records.value = records.value.filter(item => item.id !== record.id);
    if (editingId.value === record.id) reset();
    deleteId.value = null;
  } catch (error) {
    useAlert(errorText(error));
  } finally {
    saving.value = false;
  }
};
const test = async () => {
  testSequence += 1;
  const sequence = testSequence;
  testing.value = true;
  testError.value = '';
  testResult.value = null;
  testDiagnostics.value = null;
  try {
    const { data } = await BusinessAPI.test(
      editingId.value,
      form.value.customer_id_source
        ? { contact_id: testContactId.value }
        : { customer_id: testCustomerId.value },
      currentTestConfiguration()
    );
    if (sequence !== testSequence) return;
    testResult.value = data;
    testDiagnostics.value = data.diagnostics;
    paths.value = data.available_paths;
    form.value.fields = mergeResponseFields(
      form.value.fields,
      data.available_paths,
      data.diagnostics.response.body,
      form.value.response_root
    );
  } catch (error) {
    if (sequence !== testSequence) return;
    testError.value = errorText(error);
    testDiagnostics.value = error.response?.data?.diagnostics || null;
    paths.value = error.response?.data?.available_paths || [];
  } finally {
    if (sequence === testSequence) testing.value = false;
  }
};
onMounted(load);
</script>

<template>
  <SettingsLayout
    :is-loading="loading"
    :loading-message="t('BUSINESS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('BUSINESS.TITLE')"
        :description="t('BUSINESS.DESCRIPTION')"
      />
    </template>
    <template #body>
      <div v-if="failed" role="alert" class="text-n-ruby-11">
        <p>{{ t('BUSINESS.LOAD_ERROR') }}</p>
        <Button :label="t('BUSINESS.RETRY')" @click="load" />
      </div>
      <div v-else class="flex flex-col gap-5 pb-8">
        <div class="flex gap-3">
          <Button
            :label="t('BUSINESS.NEW')"
            :disabled="saving"
            @click="reset"
          /><Button
            :label="t('BUSINESS.LOAD_EXAMPLE')"
            color="slate"
            :disabled="saving"
            @click="loadExample"
          />
        </div>
        <p class="text-sm text-n-slate-11">{{ t('BUSINESS.DEMO_HINT') }}</p>
        <section
          v-if="records.length"
          class="border border-n-weak rounded-xl divide-y divide-n-weak"
        >
          <div
            v-for="record in records"
            :key="record.id"
            class="flex flex-wrap justify-between gap-3 p-4"
          >
            <div>
              <h2 class="font-medium text-n-slate-12">{{ record.name }}</h2>
              <p class="text-sm text-n-slate-11">
                {{ record.base_url }} ·
                {{
                  t(record.enabled ? 'BUSINESS.ENABLED' : 'BUSINESS.DISABLED')
                }}
              </p>
            </div>
            <div class="flex flex-wrap gap-2">
              <Button
                :label="t('BUSINESS.EDIT')"
                color="slate"
                :disabled="saving"
                @click="edit(record)"
              />
              <Button
                :label="
                  t(record.enabled ? 'BUSINESS.DISABLE' : 'BUSINESS.ENABLE')
                "
                color="slate"
                :disabled="saving"
                @click="toggle(record)"
              />
              <Button
                v-if="deleteId !== record.id"
                :label="t('BUSINESS.DELETE')"
                color="slate"
                :disabled="saving"
                @click="deleteId = record.id"
              />
              <template v-else>
                <Button
                  :label="t('BUSINESS.CONFIRM_DELETE')"
                  color="ruby"
                  :disabled="saving"
                  @click="remove(record)"
                /><Button
                  :label="t('BUSINESS.CANCEL')"
                  color="slate"
                  @click="deleteId = null"
                />
              </template>
            </div>
          </div>
        </section>
        <form
          class="grid gap-4 p-5 rounded-xl border border-n-weak bg-n-background"
          @submit.prevent="save"
        >
          <h2 class="text-heading-3 text-n-slate-12">
            {{ t(editingId ? 'BUSINESS.EDIT' : 'BUSINESS.NEW') }}
          </h2>
          <fieldset :disabled="saving" class="grid sm:grid-cols-2 gap-4">
            <label class="text-sm text-n-slate-12 flex flex-col gap-1"
              >{{ t('BUSINESS.NAME')
              }}<input
                v-model="form.name"
                type="text"
                class="w-full min-w-0"
                required
                maxlength="100"
            /></label>
            <label class="text-sm text-n-slate-12 flex flex-col gap-1"
              >{{ t('BUSINESS.BASE_URL')
              }}<input
                v-model="form.base_url"
                required
                type="url"
                maxlength="500"
                :placeholder="t('BUSINESS.EXAMPLE_URL')"
            /></label>
            <label
              class="sm:col-span-2 text-sm text-n-slate-12 flex flex-col gap-1"
              >{{ t('BUSINESS.CUSTOMER_PATH')
              }}<input
                v-model="form.customer_path"
                type="text"
                required
                maxlength="1000"
              /><span class="text-n-slate-11">{{
                t('BUSINESS.PATH_HINT', { placeholder: '{customer_id}' })
              }}</span></label
            >
            <label class="text-sm text-n-slate-12 flex flex-col gap-1"
              >{{ t('BUSINESS.AUTH')
              }}<select v-model="form.auth_type">
                <option value="none">{{ t('BUSINESS.AUTH_NONE') }}</option>
                <option value="bearer">{{ t('BUSINESS.AUTH_BEARER') }}</option>
                <option value="api_key">
                  {{ t('BUSINESS.AUTH_API_KEY') }}
                </option>
                <option value="basic">{{ t('BUSINESS.AUTH_BASIC') }}</option>
              </select></label
            >
            <label
              v-if="form.auth_type === 'api_key'"
              class="text-sm text-n-slate-12 flex flex-col gap-1"
              >{{ t('BUSINESS.AUTH_HEADER')
              }}<input
                v-model="form.auth_header"
                type="text"
                class="w-full min-w-0"
                required
                maxlength="80"
            /></label>
            <label
              v-if="form.auth_type === 'basic'"
              class="text-sm text-n-slate-12 flex flex-col gap-1"
              >{{ t('BUSINESS.USERNAME')
              }}<input
                v-model="form.username"
                type="text"
                autocomplete="off"
                maxlength="256"
            /></label>
            <label
              v-if="form.auth_type !== 'none'"
              class="text-sm text-n-slate-12 flex flex-col gap-1"
              >{{ t('BUSINESS.SECRET')
              }}<input
                v-model="form.secret"
                type="password"
                autocomplete="new-password"
                maxlength="4096"
                :placeholder="t('BUSINESS.SECRET_HINT')"
            /></label>
            <p class="sm:col-span-2 text-sm text-n-slate-11">
              {{
                t(
                  form.credential_configured
                    ? 'BUSINESS.CREDENTIAL_SET'
                    : 'BUSINESS.CREDENTIAL_EMPTY'
                )
              }}
              {{ t('BUSINESS.CREDENTIAL_HINT') }}
            </p>
            <label
              v-if="form.credential_configured"
              class="sm:col-span-2 flex gap-2 items-center text-sm text-n-slate-12"
              ><input v-model="form.clear_credentials" type="checkbox" />{{
                t('BUSINESS.CLEAR_CREDENTIAL')
              }}</label
            >
            <label class="text-sm text-n-slate-12 flex flex-col gap-1"
              >{{ t('BUSINESS.RESPONSE_ROOT')
              }}<input
                v-model="form.response_root"
                type="text"
                maxlength="200"
                :placeholder="t('BUSINESS.EXAMPLE_ROOT')"
            /></label>
            <label class="text-sm text-n-slate-12 flex flex-col gap-1"
              >{{ t('BUSINESS.CUSTOMER_ID_PATH')
              }}<input
                v-model="form.customer_id_path"
                type="text"
                class="w-full min-w-0"
                required
                maxlength="200"
            /></label>
            <label
              class="sm:col-span-2 text-sm text-n-slate-12 flex flex-col gap-1"
              >{{ t('BUSINESS.CUSTOMER_ID_SOURCE')
              }}<select v-model="customerIdSourceType">
                <option value="">{{ t('BUSINESS.SOURCE_MANUAL') }}</option>
                <option value="email">{{ t('BUSINESS.SOURCE_EMAIL') }}</option>
                <option value="phone_number">
                  {{ t('BUSINESS.SOURCE_PHONE') }}
                </option>
                <option value="identifier">
                  {{ t('BUSINESS.SOURCE_IDENTIFIER') }}
                </option>
                <option value="name">
                  {{ t('BUSINESS.SOURCE_NAME') }}
                </option>
                <option value="custom">
                  {{ t('BUSINESS.SOURCE_CUSTOM') }}
                </option>
              </select>
              <span class="text-n-slate-11">{{
                t('BUSINESS.CUSTOMER_ID_SOURCE_HINT', {
                  placeholder: '{customer_id}',
                })
              }}</span></label
            >
            <label
              v-if="customerIdSourceType === 'custom'"
              class="sm:col-span-2 text-sm text-n-slate-12 flex flex-col gap-1"
            >
              {{ t('BUSINESS.CUSTOMER_ID_CUSTOM_FIELD') }}
              <input
                v-model.trim="customCustomerIdField"
                type="text"
                required
                maxlength="100"
                pattern="[a-zA-Z0-9_-]+"
                :placeholder="t('BUSINESS.EXAMPLE_CUSTOMER_ID_SOURCE')"
              />
            </label>
            <label
              class="sm:col-span-2 text-sm text-n-slate-12 flex flex-col gap-1"
              >{{ t('BUSINESS.IDENTITY_PREFIX')
              }}<input
                v-model="form.identifier_prefix"
                type="text"
                maxlength="100"
                :placeholder="t('BUSINESS.EXAMPLE_PREFIX')"
              /><span class="text-n-slate-11">{{
                t('BUSINESS.IDENTITY_HINT')
              }}</span></label
            >
            <label class="flex gap-2 items-center text-sm text-n-slate-12"
              ><input v-model="form.enabled" type="checkbox" />{{
                t('BUSINESS.ENABLE')
              }}</label
            >
          </fieldset>
          <section class="p-4 border border-n-weak rounded-xl grid gap-4">
            <h3 class="text-heading-3 text-n-slate-12">
              {{ t('BUSINESS.TEST_CURRENT') }}
            </h3>
            <p class="text-sm text-n-slate-11">
              {{ t('BUSINESS.TEST_CURRENT_HINT') }}
            </p>
            <div class="flex flex-wrap items-end gap-3">
              <div
                v-if="form.customer_id_source"
                class="min-w-0 w-full max-w-md space-y-2"
              >
                <p class="mb-0 text-sm text-n-slate-12">
                  {{ t('BUSINESS.TEST_CONTACT') }}
                </p>
                <ComboBox
                  v-model="testContactId"
                  :options="testContactOptions"
                  :placeholder="t('BUSINESS.TEST_CONTACT_PLACEHOLDER')"
                  :search-placeholder="t('BUSINESS.TEST_CONTACT_SEARCH')"
                  :empty-state="
                    t(
                      contactsLoading
                        ? 'BUSINESS.LOADING'
                        : 'BUSINESS.TEST_CONTACT_EMPTY'
                    )
                  "
                  :disabled="testing || saving"
                  use-api-results
                  @open="fetchTestContacts()"
                  @search="searchTestContacts"
                />
                <p
                  v-if="contactSearchError"
                  role="alert"
                  class="text-sm text-n-ruby-11"
                >
                  {{ t('BUSINESS.TEST_CONTACT_ERROR') }}
                </p>
              </div>
              <label v-else class="text-sm text-n-slate-12 flex flex-col gap-1">
                {{ t('BUSINESS.CUSTOMER_ID') }}
                <input
                  v-model="testCustomerId"
                  type="text"
                  maxlength="200"
                  @keydown.enter.prevent="test"
                />
              </label>
              <Button
                type="button"
                :label="t('BUSINESS.RUN_TEST_CURRENT')"
                :disabled="testing || saving || !canTest"
                :is-loading="testing"
                @click="test"
              />
            </div>
            <p v-if="testError" role="alert" class="text-sm text-n-ruby-11">
              {{ testError }}
            </p>
            <p v-if="testResult" role="status" class="text-sm text-n-slate-12">
              {{ t('BUSINESS.TEST_CURRENT_OK') }}
              {{
                t('BUSINESS.TEST_RESOLVED_CUSTOMER_ID', {
                  customerId: testResult.customer_id,
                })
              }}
            </p>
            <details v-if="paths.length">
              <summary class="cursor-pointer text-sm text-n-slate-12">
                {{ t('BUSINESS.AVAILABLE_PATHS') }}
              </summary>
              <pre class="mt-3 text-xs text-n-slate-11 whitespace-pre-wrap">{{
                paths.join('\n')
              }}</pre>
            </details>
            <section
              v-if="testDiagnostics"
              class="grid gap-4 border-t border-n-weak pt-4"
            >
              <h4 class="text-sm font-medium text-n-slate-12">
                {{ t('BUSINESS.DIAGNOSTICS') }}
              </h4>
              <div v-if="testDiagnostics.request" class="grid gap-2">
                <h5 class="text-sm font-medium text-n-slate-12">
                  {{ t('BUSINESS.REQUEST_DETAILS') }}
                </h5>
                <pre class="max-h-80" :class="DIAGNOSTICS_CLASS">{{
                  formatDiagnostics(testDiagnostics.request)
                }}</pre>
              </div>
              <div v-if="testDiagnostics.response" class="grid gap-2">
                <h5 class="text-sm font-medium text-n-slate-12">
                  {{ t('BUSINESS.RESPONSE_DETAILS') }}
                </h5>
                <pre class="max-h-96" :class="DIAGNOSTICS_CLASS">{{
                  formatDiagnostics(testDiagnostics.response)
                }}</pre>
              </div>
            </section>
          </section>
          <FieldEditor
            v-model:fields="form.fields"
            :disabled="saving"
            :paths="paths"
          />
          <div class="flex gap-3">
            <Button
              type="submit"
              :label="t('BUSINESS.SAVE')"
              :disabled="saving || !form.fields.length"
              :is-loading="saving"
            /><Button
              :label="t('BUSINESS.CANCEL')"
              color="slate"
              :disabled="saving"
              @click="reset"
            />
          </div>
        </form>
      </div>
    </template>
  </SettingsLayout>
</template>
